import React from 'react';
import { Dialog, Box, Typography, Button, IconButton, Chip, Divider, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const folioDe = (id) => '#' + String(id || '').slice(-6).toUpperCase();
const hh = (n) => String(n).padStart(2, '0');

// "05 mar 2026 - 14:30"
const fmtCorto = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha);
  if (isNaN(d)) return '—';
  return `${hh(d.getDate())} ${MESES_CORTO[d.getMonth()]} ${d.getFullYear()} - ${hh(d.getHours())}:${hh(d.getMinutes())}`;
};

// "9 de marzo de 2026 a las 10:00 hrs"
const fmtLargo = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha);
  if (isNaN(d)) return '—';
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()} a las ${hh(d.getHours())}:${hh(d.getMinutes())} hrs`;
};

const FACT_LABEL = {
  no_solicitada: 'No solicitada',
  solicitada: 'Factura solicitada',
  emitida: 'Facturado'
};

// Fila de la línea de tiempo (icono en círculo + label + valor)
const HitoTiempo = ({ icon: Icon, color, bg, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon sx={{ fontSize: 18, color }} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: 13, color: '#737791' }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0E2465' }}>{value}</Typography>
    </Box>
  </Box>
);

// Fila de detalles financieros
const FilaFinanciera = ({ label, value, total }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: total ? 1.2 : 0.8 }}>
    <Typography sx={{ fontSize: total ? 15 : 13.5, fontWeight: total ? 800 : 500, color: total ? '#0E2465' : '#737791' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: total ? 17 : 14, fontWeight: total ? 900 : 700, color: '#0E2465' }}>
      {value}
    </Typography>
  </Box>
);

const DetalleReservaModal = ({ open, onClose, booking, personaNombre, onReportar, enRetencion = false, puedeReportar = true }) => {
  if (!booking) return null;

  const unidad = booking.tipoCosto
    ? booking.tipoCosto.charAt(0).toUpperCase() + booking.tipoCosto.slice(1)
    : (booking.cotizacion ? 'Proyecto' : 'Servicio');
  const costo = Number(booking.costoServicio) || 0;
  const total = booking.pago?.monto != null ? booking.pago.monto : costo;
  const dinero = (n) => '$' + (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const estadoFactura = booking.factura?.estado || 'no_solicitada';
  // Aprobado y Finalizado usan los timestamps reales; en reservas anteriores a este
  // cambio no existen, así que se aproximan con createdAt / updatedAt para no salir vacíos.
  const aprobado = booking.fechaConfirmacion || booking.createdAt;
  // Finalizado = momento en que se dio click en "finalizar servicio" (fechaCompletado);
  // en reservas viejas cae al updatedAt (última modificación ≈ cuando se completó).
  const finalizado = booking.fechaCompletado || booking.updatedAt;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '22px', overflow: 'hidden' } } }}>
      {/* ---- Header ---- */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#EEF2FF', px: 2.5, py: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DescriptionOutlinedIcon sx={{ color: '#7495F8' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0E2465' }}>Detalle de la reserva</Typography>
          <Typography sx={{ fontSize: 12, color: '#737791' }}>ID de Reserva: {folioDe(booking._id)}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#737791' }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0E2465', mb: 1 }}>
          {booking.servicioNombre || 'Servicio'}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Chip label="Finalizado" size="small" sx={{ bgcolor: '#E0E7FF', color: '#3730A3', fontWeight: 700, fontSize: 12 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#737791' }}>
            <PersonOutlineIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 13.5, color: '#0E2465', fontWeight: 600 }}>{personaNombre}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ borderColor: '#F1F5F9', mb: 2.5 }} />

        {/* ---- Línea de tiempo ---- */}
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0E2465', letterSpacing: 0.5, mb: 2 }}>
          LÍNEA DE TIEMPO
        </Typography>
        <HitoTiempo icon={EventAvailableOutlinedIcon} color="#7495F8" bg="#EEF2FF"
          label="Servicio Solicitado" value={fmtCorto(booking.createdAt)} />
        <HitoTiempo icon={CheckCircleOutlineIcon} color="#1B7A5A" bg="#D6F3E8"
          label="Servicio Aprobado" value={fmtCorto(aprobado)} />
        <HitoTiempo icon={AccessTimeIcon} color="#7495F8" bg="#EEF2FF"
          label="Finalizado" value={fmtLargo(finalizado)} />

        {/* ---- Detalles financieros ---- */}
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0E2465', letterSpacing: 0.5, mt: 1, mb: 1.5 }}>
          DETALLES FINANCIEROS
        </Typography>
        <Box sx={{ bgcolor: '#F8FAFC', borderRadius: '14px', p: 2 }}>
          <FilaFinanciera label="Unidad de cobro" value={unidad} />
          <FilaFinanciera label={`Costo por ${unidad.toLowerCase()}`} value={dinero(costo)} />
          <Divider sx={{ borderColor: '#E2E8F0', my: 0.5 }} />
          <FilaFinanciera label="Costo Total" value={dinero(total)} total />
          <Divider sx={{ borderColor: '#E2E8F0', my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#737791' }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13.5, color: '#737791' }}>Estado de facturación</Typography>
            </Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: estadoFactura === 'emitida' ? '#1B7A5A' : '#737791' }}>
              {FACT_LABEL[estadoFactura]}
            </Typography>
          </Box>
        </Box>
        {/* Ventana de retención: el pago sigue en custodia; un reporte lo congela */}
        {enRetencion && (
          <Box sx={{ mt: 2, bgcolor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '12px', p: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, color: '#9A3412', fontWeight: 600 }}>
              Tu pago aún está retenido. Si algo salió mal con el servicio, repórtalo ahora:
              se congelará hasta que el equipo lo revise. Después del corte semanal ya no
              podrás abrir un reporte, solo una aclaración.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ---- Footer ---- */}
      <Box sx={{ display: 'flex', gap: 1.5, px: 3, pb: 3 }}>
        {/* El botón de reportar/aclarar solo aparece dentro de la ventana permitida
            (últimos 3 meses); en servicios más viejos ya no se muestra. */}
        {puedeReportar && (
          <Button fullWidth variant="contained" disableElevation onClick={onReportar}
            sx={{ bgcolor: '#FF827A', color: 'white', textTransform: 'none', fontWeight: 700, borderRadius: '12px', py: 1.3,
              '&:hover': { bgcolor: '#e06f68' } }}>
            {enRetencion ? 'Reportar problema' : 'Reportar / Aclaración'}
          </Button>
        )}
        <Button fullWidth variant="contained" disableElevation onClick={onClose}
          sx={{ bgcolor: '#0E2465', color: 'white', textTransform: 'none', fontWeight: 700, borderRadius: '12px', py: 1.3,
            '&:hover': { bgcolor: '#0a1a4a' } }}>
          Cerrar detalle
        </Button>
      </Box>
    </Dialog>
  );
};

export default DetalleReservaModal;
